import apiClient from './apiService';

export type BrdStatus = 'DRAFT' | 'IN_REVIEW' | 'FINALISED' | 'REJECTED';
export type BrdVote = 'UPVOTE' | 'DOWNVOTE' | 'RESERVE';
export type TestStatus = 'NOT_RUN' | 'PASS' | 'FAIL' | 'BLOCKED';
export type TicketStatus = 'BACKLOG' | 'TODO' | 'IN_PROGRESS' | 'REVIEW' | 'DONE';
export type TicketCriticality = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';

export interface ProjectResource {
  id: number; project_id: number;
  resource_kind?: 'STAFF' | 'MACHINE';
  user_id?: number | null; it_asset_id?: number | null;
  role_in_project?: string; is_active: number;
  hourly_rate?: number; budgeted_hours?: number;
  user_name?: string; login_id?: string; resource_name?: string;
}

export interface BrdTag {
  id: number; project_id: number; name: string; color?: string; is_active: number;
}

export interface BrdItem {
  id: number; project_id: number; task_id: number;
  sequence: number; content: string; status: BrdStatus;
  finalised_by?: number | null; finalised_on?: string | null;
  is_active: number;
  tags?: BrdTag[];
  upvotes?: number; downvotes?: number; reserves?: number;
}

export interface BrdVoteRow {
  id: number; brd_item_id: number; user_id: number;
  vote: BrdVote; comment?: string; voted_on?: string; user_name?: string;
}

export interface TestCase {
  id: number; project_id: number; brd_item_id: number;
  title: string; preconditions?: string; steps?: string; expected_result?: string;
  current_status: TestStatus; iteration_count: number;
  last_run_on?: string; last_run_by?: number; assignee_id?: number;
  is_active: number;
}

export interface TestRun {
  id: number; test_case_id: number; iteration_no: number;
  result: TestStatus; notes?: string; run_by?: number; run_on?: string;
}

export interface Ticket {
  id: number; project_id: number; brd_item_id: number;
  title: string; description?: string;
  criticality: TicketCriticality; status: TicketStatus;
  assignee_id?: number; reporter_id?: number;
  due_date?: string; opened_on?: string; closed_on?: string;
  is_active: number;
  linked_test_case_ids?: number[];
}

const base = '/api/v1/prm';

// Resources
export const listResources = (projectId: number) =>
  apiClient.get<ProjectResource[]>(`${base}/projects/${projectId}/resources`).then(r => r.data);
export const addResource = (projectId: number, body: { resource_kind?: 'STAFF' | 'MACHINE'; user_id?: number; it_asset_id?: number; role_in_project?: string; hourly_rate?: number; budgeted_hours?: number }) =>
  apiClient.post<ProjectResource>(`${base}/projects/${projectId}/resources`, body).then(r => r.data);
export const removeResource = (projectId: number, resourceId: number) =>
  apiClient.delete(`${base}/projects/${projectId}/resources/${resourceId}`);

// Tags
export const listTags = (projectId: number) =>
  apiClient.get<BrdTag[]>(`${base}/projects/${projectId}/brd-tags`).then(r => r.data);
export const createTag = (projectId: number, body: { name: string; color?: string }) =>
  apiClient.post<BrdTag>(`${base}/projects/${projectId}/brd-tags`, body).then(r => r.data);

// BRD items
export const listBrdItems = (taskId: number) =>
  apiClient.get<BrdItem[]>(`${base}/tasks/${taskId}/brd-items`).then(r => r.data);
export const createBrdItem = (body: { task_id: number; content: string; sequence?: number; tag_ids?: number[] }) =>
  apiClient.post<BrdItem>(`${base}/brd-items`, body).then(r => r.data);
export const updateBrdItem = (id: number, body: Partial<{ content: string; sequence: number; status: BrdStatus; tag_ids: number[] }>) =>
  apiClient.put<BrdItem>(`${base}/brd-items/${id}`, body).then(r => r.data);
export const deleteBrdItem = (id: number) =>
  apiClient.delete(`${base}/brd-items/${id}`);
export const finaliseBrdItem = (id: number) =>
  apiClient.post<BrdItem>(`${base}/brd-items/${id}/finalise`).then(r => r.data);

// Votes
export const listVotes = (brdId: number) =>
  apiClient.get<BrdVoteRow[]>(`${base}/brd-items/${brdId}/votes`).then(r => r.data);
export const upsertVote = (brdId: number, body: { vote: BrdVote; comment?: string }) =>
  apiClient.post<BrdVoteRow>(`${base}/brd-items/${brdId}/vote`, body).then(r => r.data);

// Test cases
export const listTestCases = (brdId: number) =>
  apiClient.get<TestCase[]>(`${base}/brd-items/${brdId}/test-cases`).then(r => r.data);
export const createTestCase = (body: { brd_item_id: number; title: string; preconditions?: string; steps?: string; expected_result?: string; assignee_id?: number }) =>
  apiClient.post<TestCase>(`${base}/test-cases`, body).then(r => r.data);
export const updateTestCase = (id: number, body: Partial<{ title: string; preconditions: string; steps: string; expected_result: string; assignee_id: number }>) =>
  apiClient.put<TestCase>(`${base}/test-cases/${id}`, body).then(r => r.data);
export const deleteTestCase = (id: number) =>
  apiClient.delete(`${base}/test-cases/${id}`);
export const runTestCase = (id: number, body: { result: TestStatus; notes?: string }) =>
  apiClient.post<TestRun>(`${base}/test-cases/${id}/run`, body).then(r => r.data);
export const listTestRuns = (id: number) =>
  apiClient.get<TestRun[]>(`${base}/test-cases/${id}/runs`).then(r => r.data);

// Tickets
export const listTickets = (brdId: number) =>
  apiClient.get<Ticket[]>(`${base}/brd-items/${brdId}/tickets`).then(r => r.data);
export const createTicket = (body: { brd_item_id: number; title: string; description?: string; criticality?: TicketCriticality; status?: TicketStatus; assignee_id?: number; due_date?: string; test_case_ids?: number[] }) =>
  apiClient.post<Ticket>(`${base}/tickets`, body).then(r => r.data);
export const updateTicket = (id: number, body: Partial<{ title: string; description: string; criticality: TicketCriticality; status: TicketStatus; assignee_id: number; due_date: string }>) =>
  apiClient.put<Ticket>(`${base}/tickets/${id}`, body).then(r => r.data);
export const deleteTicket = (id: number) =>
  apiClient.delete(`${base}/tickets/${id}`);
export const linkTicketTest = (ticketId: number, testCaseId: number) =>
  apiClient.post(`${base}/tickets/${ticketId}/link-test/${testCaseId}`);
export const unlinkTicketTest = (ticketId: number, testCaseId: number) =>
  apiClient.delete(`${base}/tickets/${ticketId}/link-test/${testCaseId}`);

// Tasks (re-use existing endpoint)
export const listProjectTasks = (projectId: number) =>
  apiClient.get<any[]>(`${base}/projects/${projectId}/tasks`).then(r => r.data);
